import React from 'react';
import { LIFECYCLE_LABEL } from '../lib/assets';

/**
 * A device on the map: an icon for what it is, inside a ring for how it is.
 *
 * Gordon, 27 October: "icons showing the item instead of the whole asset
 * register code (IT-LAP-000002) ... cleaner and take less space", with a
 * coloured ring. Agreed rule, first match wins:
 *
 *   red     faulty, or retired (retired devices show only with "Show retired")
 *   grey    not in use: in stock, and ordered, received or in repair
 *   amber   in use but not seen for 30 days or more - seen being the last
 *           heartbeat, or the last time a scan or a laptop's report saw it
 *   green   in use
 *
 * A device in use that has never reported (a printer nothing has scanned)
 * is green, not amber: amber is for something that WAS seen and went
 * quiet, so a new register does not light up amber on day one.
 */
export const QUIET_DAYS = 30;

const DAY = 86400000;

export function pinStatus(d, now = Date.now()) {
  if (d.operational_status === 'faulty') return { tone: 'critical', label: 'Faulty' };
  if (d.lifecycle_status === 'retired' || d.lifecycle_status === 'disposed') {
    return { tone: 'critical', label: LIFECYCLE_LABEL[d.lifecycle_status] };
  }
  if (d.lifecycle_status !== 'deployed') return { tone: 'idle', label: LIFECYCLE_LABEL[d.lifecycle_status] || 'Not in use' };
  const seen = [d.last_seen_at, d.last_heartbeat_at].filter(Boolean).map((t) => new Date(t).getTime()).sort((a, b) => b - a)[0];
  if (seen && now - seen >= QUIET_DAYS * DAY) {
    const days = Math.floor((now - seen) / DAY);
    return { tone: 'warning', label: `Not seen for ${days} days` };
  }
  return { tone: 'success', label: seen ? 'In use' : 'In use · never reported' };
}

export const RING_LEGEND = [
  { tone: 'success', label: 'In use' },
  { tone: 'warning', label: `Not seen for ${QUIET_DAYS} days` },
  { tone: 'critical', label: 'Faulty or retired' },
  { tone: 'idle', label: 'In stock / not in use' }
];

/* Line icons on a 24-unit grid, drawn with the current colour. */
const PATHS = {
  laptop: 'M5 6h14v9H5z M3 18h18',
  desktop: 'M4 4h11v8H4z M9.5 12v4 M6.5 16h6 M17 4h3v14h-3z',
  monitor: 'M3 4h18v12H3z M12 16v4 M8 20h8',
  dock: 'M4 9h16v6H4z M7 12h.01 M10 12h.01 M13 12h4',
  printer: 'M7 9V4h10v5 M4 9h16v7H4z M7 14h10v6H7z',
  network: 'M3 8h18v8H3z M6.5 12h.01 M9.5 12h.01 M12.5 12h.01 M15.5 12h.01 M18 12h.01',
  wifi: 'M4 10a12 12 0 0 1 16 0 M7 13a7.5 7.5 0 0 1 10 0 M10 16a3 3 0 0 1 4 0 M12 19h.01',
  server: 'M5 4h14v5H5z M5 10h14v5H5z M5 16h14v4H5z M8 6.5h.01 M8 12.5h.01',
  storage: 'M5 5h14v14H5z M5 12h14 M8 8.5h.01 M8 15.5h.01',
  phone: 'M8 3h8v18H8z M11 18h2',
  camera: 'M4 8h11v8H4z M15 11l5-3v8l-5-3',
  peripheral: 'M3 8h18v9H3z M6 11h.01 M9 11h.01 M12 11h.01 M15 11h.01 M18 11h.01 M7 14h10',
  iot: 'M4 11l8-7 8 7 M6 10v10h12V10 M12 14h.01',
  power: 'M6 6h12v14H6z M12 9v4 M10 11h4',
  other: 'M5 5h14v14H5z M9 12h6'
};

/** Which icon a category gets, by its name - the register's categories are named, not coded. */
export function iconFor(categoryName) {
  const n = String(categoryName || '').toLowerCase();
  if (/laptop|notebook/.test(n)) return 'laptop';
  if (/desktop|workstation|\bpc\b/.test(n)) return 'desktop';
  if (/monitor|screen|display/.test(n)) return 'monitor';
  if (/dock/.test(n)) return 'dock';
  if (/print|scan|copier/.test(n)) return 'printer';
  if (/access point|wi-?fi|wireless/.test(n)) return 'wifi';
  if (/network|switch|router|firewall/.test(n)) return 'network';
  if (/server/.test(n)) return 'server';
  if (/storage|nas|drive|disk/.test(n)) return 'storage';
  if (/phone|mobile|tablet/.test(n)) return 'phone';
  if (/camera|webcam|cctv/.test(n)) return 'camera';
  if (/\biot\b|smart plug|air ?con|thermostat|sensor/.test(n)) return 'iot';
  if (/ups|power/.test(n)) return 'power';
  if (/peripheral|keyboard|mouse|headset/.test(n)) return 'peripheral';
  return 'other';
}

export function CategoryIcon({ category, className = 'as-pin-icon' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon={iconFor(category)}>
      <path d={PATHS[iconFor(category)]} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
